import React, { useState, useEffect } from 'react';
import { socketService } from '../utils/socket';
import { localStorageManager } from '../utils/localStorage';
import SocketNotification from '../components/SocketNotification';
import './PodiumPage.css';

interface PodiumConfig {
  title: string;
  subtitle: string;
  teams: {
    first?: string;
    second?: string;
    third?: string;
  };
  teamImages: {
    [teamName: string]: string;
  };
  showPodium: boolean;
  leaderboard?: any;
  bracket?: any;
}

const PodiumPage: React.FC = () => {
  const [config, setConfig] = useState<PodiumConfig>({
    title: 'Podium de Ganadores',
    subtitle: 'Neurolimpiadas 2025',
    teams: {},
    teamImages: {},
    showPodium: false
  });
  const [showThird, setShowThird] = useState(false);
  const [showSecond, setShowSecond] = useState(false);
  const [showFirst, setShowFirst] = useState(false);
  
  // Claves únicas para forzar re-renderizado de animaciones
  const [animationKeys, setAnimationKeys] = useState({
    third: 0,
    second: 0,
    first: 0
  });

  // Cargar configuración inicial
  useEffect(() => {
    const savedConfig = localStorageManager.getConfig();
    if (savedConfig.podium) {
      setConfig(prev => ({
        ...prev,
        ...savedConfig.podium,
        leaderboard: savedConfig.leaderboard,
        bracket: savedConfig.bracket
      }));
      console.log('🏆 Configuración inicial del podium cargada:', savedConfig.podium);
    }
  }, []);

  // Event listeners
  useEffect(() => {
    const handleConfigUpdate = (data: any) => {
      console.log('🏆 PodiumPage - Configuración recibida:', data);
      if (data.section === 'podium') {
        console.log('🏆 Actualizando configuración del podium:', data.data);
        setConfig(prev => ({ ...prev, ...data.data }));
      }
      // También actualizar cuando cambien leaderboard o bracket
      if (data.section === 'leaderboard') {
        setConfig(prev => ({ ...prev, leaderboard: data.data }));
      }
      if (data.section === 'bracket') {
        setConfig(prev => ({ ...prev, bracket: data.data }));
      }
    };    const handlePodiumCommand = (command: any) => {
      console.log('🏆 PodiumPage - Comando recibido:', command);
      const { command: cmd } = command;
      
      switch (cmd) {
        case 'showPodium':
          console.log('🏆 Mostrando podium...');
          setConfig(prev => ({ ...prev, showPodium: true }));
          // No mostrar equipos automáticamente
          break;        case 'showThird':
          console.log('🏆 Mostrando tercer lugar...');
          setConfig(prev => ({ ...prev, showPodium: true }));
          setAnimationKeys(prev => ({ ...prev, third: prev.third + 1 }));
          setShowThird(true);
          break;
        case 'showSecond':
          console.log('🏆 Mostrando segundo lugar...');
          setAnimationKeys(prev => ({ ...prev, second: prev.second + 1 }));
          setShowSecond(true);
          break;
        case 'showFirst':
          console.log('🏆 Mostrando primer lugar...');
          setAnimationKeys(prev => ({ ...prev, first: prev.first + 1 }));
          setShowFirst(true);
          break;
        case 'hideThird':
          console.log('🏆 Ocultando tercer lugar...');
          setShowThird(false);
          break;
        case 'hideSecond':
          console.log('🏆 Ocultando segundo lugar...');
          setShowSecond(false);
          break;
        case 'hideFirst':
          console.log('🏆 Ocultando primer lugar...');
          setShowFirst(false);
          break;        case 'hidePodium':
          console.log('🏆 Ocultando podium...');
          setConfig(prev => ({ ...prev, showPodium: false }));
          setShowThird(false);
          setShowSecond(false);
          setShowFirst(false);
          setAnimationKeys({ third: 0, second: 0, first: 0 });
          break;        case 'reset':
          console.log('🏆 Reiniciando podium...');
          setConfig(prev => ({ 
            ...prev, 
            teams: {}, 
            showPodium: false 
          }));
          setShowThird(false);
          setShowSecond(false);
          setShowFirst(false);
          setAnimationKeys({ third: 0, second: 0, first: 0 });
          break;
        case 'prevSlide':
          console.log('🏆 Comando prevSlide - ocultando todos los elementos');
          setShowFirst(false);
          setShowSecond(false);
          setShowThird(false);
          break;
        case 'nextSlide':
          console.log('🏆 Comando nextSlide - no implementado específicamente');
          // Podrías agregar lógica específica aquí si necesitas un comportamiento especial
          break;
        default:
          console.log('❓ Comando no reconocido:', cmd);
      }
    };

    // Manejar comandos generales de navegación
    const handleGeneralCommand = (command: any) => {
      console.log('🏆 PodiumPage - Comando general recibido:', command);
      const { command: cmd } = command;
      
      switch (cmd) {
        case 'prev':
          console.log('⬅️ Comando PREV en podium - ocultando todos los elementos');
          setShowFirst(false);
          setShowSecond(false);
          setShowThird(false);
          break;
        case 'next':
          console.log('➡️ Comando NEXT en podium - no implementado específicamente');
          // Podrías agregar lógica específica aquí si necesitas un comportamiento especial
          break;
        default:
          // Ignorar otros comandos
          break;
      }
    };

    socketService.on('configUpdate', handleConfigUpdate);
    socketService.on('podiumCommand', handlePodiumCommand);
    socketService.on('timerCommand', handleGeneralCommand); // Escuchar comandos generales

    return () => {
      socketService.off('configUpdate', handleConfigUpdate);
      socketService.off('podiumCommand', handlePodiumCommand);
      socketService.off('timerCommand', handleGeneralCommand);
    };
  }, []);

  // Mapeo de equipos a municipios
  const getTeamMunicipality = (teamName: string): string => {
    const teamToMunicipality: { [key: string]: string } = {
      // Güímar
      'Chinguaro': 'Güímar',
      'Acaymo': 'Güímar',
      'Arena': 'Güímar',
      'Costeras': 'Güímar',
      
      // La Victoria
      'Las Guays': 'La Victoria',
      'Reinas valientes': 'La Victoria',
      'Arroyadoras': 'La Victoria',
      'Tagoro': 'La Victoria',
      
      // Santa Úrsula
      'Piña': 'Santa Úrsula',
      'Corujera': 'Santa Úrsula',
      'Guayota': 'Santa Úrsula',
      'Yeyes': 'Santa Úrsula',
      'Los Cipreses': 'Santa Úrsula',
      
      // El Rosario
      'Lomo Pelado': 'El Rosario',
      'Noveleras': 'El Rosario',
      'Poderosas': 'El Rosario',
      'Perlas': 'El Rosario',
      'Las Élites': 'El Rosario',
      'Madroño': 'El Rosario'
    };
    
    return teamToMunicipality[teamName] || teamName;
  };

  const getTeamImage = (teamName: string): string => {
    if (!teamName) return '';
    
    // Obtener el municipio del equipo
    const municipality = getTeamMunicipality(teamName);
    
    // Mapeo de municipios a imágenes
    const municipalityImageMap: { [key: string]: string } = {
      'El Rosario': '/img/teams/el-rosario.png',
      'Güímar': '/img/teams/guimar.png', 
      'La Victoria': '/img/teams/la-victoria.png',
      'Santa Úrsula': '/img/teams/santa-ursula.png'
    };

    return municipalityImageMap[municipality] || '';
  };
  const renderTeamOnPodium = (position: 'third' | 'second' | 'first', show: boolean) => {
    const teamName = config.teams[position];
    const teamImage = getTeamImage(teamName || '');
    
    if (!teamName || !show) return null;

    const positionData = {
      third: { rank: 3, className: 'third-position' },
      second: { rank: 2, className: 'second-position' },
      first: { rank: 1, className: 'first-position' }
    };

    const data = positionData[position];
    const animationKey = animationKeys[position];

    return (
      <div 
        key={`${position}-${animationKey}`}
        className={`team-on-podium ${data.className} ${animationKey > 0 ? 'force-animate' : 'animate-in'}`}
      >
        {teamImage && (
          <div className="team-image-podium">
            <img 
              src={teamImage} 
              alt={`Equipo ${teamName}`}
              className="team-img"
              onError={(e) => {
                console.warn(`No se pudo cargar la imagen para ${teamName}`);
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
        
        <div className="team-info-podium">
          <div className="team-position-number">
            {data.rank}°
          </div>
          <div className="team-name-podium">{teamName}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="podium-page-fullscreen">
      <SocketNotification showOnMount={true} />
      
      {/* Imagen del podio como fondo completo */}
      <div className="podium-background-full">
        <img 
          src="/img/podio.png" 
          alt="Podium" 
          className="podium-image-fullscreen"
        />
      </div>

      {/* Header flotante */}
      <div className="podium-header-overlay">
        <h1 className="podium-title-overlay">{config.title}</h1>
        <p className="podium-subtitle-overlay">{config.subtitle}</p>
      </div>

      {/* Equipos sobre el podium */}
      <div className="teams-overlay">
        {renderTeamOnPodium('third', showThird)}
        {renderTeamOnPodium('second', showSecond)}
        {renderTeamOnPodium('first', showFirst)}
      </div>      {/* Mensaje de espera */}
      {!config.showPodium && (
        <div className="waiting-overlay">
          <div className="waiting-content">
            <h2>Esperando resultados finales...</h2>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default PodiumPage;
